import { Check, Eye, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState } from "../components/PageState";
import { QuickSearch } from "../components/QuickSearch";
import { SearchSelect } from "../components/SearchSelect";
import { formatDate, formatNumber, Message, Modal, PageHeader, StatusBadge, Tabs, today } from "../components/Ui";
import type { DocumentStatus, FoundationData, ItemListRow } from "../types";

type PlanLine = {
  id: string; lineNo: number; productItemId: string; productCode: string; productName: string;
  plannedQuantity: number; uomId: string; uomName: string; reportedQuantity: number; remainingQuantity: number;
  overPlanQuantity: number;
};
type PlanRow = {
  id: string; number: string; planDate: string; plannedStartDate?: string | null; plannedEndDate?: string | null;
  status: DocumentStatus; notes?: string | null; lineCount: number; lines: PlanLine[];
};
type ReportLine = {
  id: string; lineNo: number; productionPlanLineId?: string | null; productionPlanId?: string | null;
  planNumber?: string | null; productItemId: string; productCode: string; productName: string;
  uomId: string; uomName: string; productionLotCode: string; machineQuantity: number;
  processType: "FromBlanks" | "BoxFromWarehouseSemiFinished";
  sourceInventoryDocumentId?: string | null; sourceInventoryDocumentNumber?: string | null;
  semiFinishedQuantity: number; producedBagQuantity: number; finishedQuantity: number; rejectedQuantity: number;
  remainingBlankQuantity: number; issuedSemiFinishedQuantity: number; returnedSemiFinishedQuantity: number;
  damagedSemiFinishedQuantity: number;
};
type ReportRow = {
  id: string; number: string; reportDate: string; productionLineId?: string | null; productionLineName?: string | null;
  shiftId?: string | null; shiftName?: string | null; status: DocumentStatus; notes?: string | null;
  lineCount: number; machineQuantity: number; semiFinishedQuantity: number; finishedQuantity: number;
  rejectedQuantity: number; remainingBlankQuantity: number; lines: ReportLine[];
};
type WorkshopStock = {
  asOf: string; blankUom: string;
  items: Array<{ itemId: string; itemCode: string; itemName: string; blankBalance: number }>;
};
type SemiFinishedIssue = {
  id: string; number: string; documentDate: string; productItemId: string; productCode: string;
  productName: string; issuedQuantity: number; uomId: string; uomName: string;
};
type PlanDraftLine = {
  clientKey: string; id?: string; productItemId: string; plannedQuantity: number; uomId: string; reportedQuantity?: number;
};
type ReportDraftLine = {
  clientKey: string;
  productionPlanLineId: string; productItemId: string; uomId: string; productionLotCode: string;
  processType: "FromBlanks" | "BoxFromWarehouseSemiFinished"; sourceInventoryDocumentId: string;
  machineQuantity: number; semiFinishedQuantity: number; finishedQuantity: number; rejectedQuantity: number;
  damagedSemiFinishedQuantity: number;
};

let draftLineSequence = 0;
const nextDraftLineKey = () => `production-line-${Date.now()}-${++draftLineSequence}`;

const blankPlanLine = (): PlanDraftLine => ({ clientKey: nextDraftLineKey(), productItemId: "", plannedQuantity: 0, uomId: "", reportedQuantity: 0 });
const blankReportLine = (processType: ReportDraftLine["processType"] = "FromBlanks"): ReportDraftLine => ({
  clientKey: nextDraftLineKey(),
  productionPlanLineId: "", productItemId: "", uomId: "", productionLotCode: "",
  processType, sourceInventoryDocumentId: "", machineQuantity: 0,
  semiFinishedQuantity: 0, finishedQuantity: 0, rejectedQuantity: 0, damagedSemiFinishedQuantity: 0
});

const planProgressClass = (line: PlanLine) => line.reportedQuantity <= 0
  ? "progress-none"
  : line.remainingQuantity > 0
    ? "progress-partial"
    : "progress-complete";

export function ProductionPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("ADMIN") ?? false;
  const hasPermission = (permission: string) => isAdmin || user?.permissions.includes(permission) === true;
  const productionTabs = [
    ...(hasPermission("Planning.View") ? [{ value: "plans", label: "Kế hoạch" }] : []),
    ...(hasPermission("Production.View") ? [{ value: "reports", label: "Báo cáo sản lượng cuối ca" }, { value: "workshop", label: "Tồn tại xưởng" }] : [])
  ];
  const [tab, setTab] = useState<"plans" | "reports" | "workshop">(() => hasPermission("Planning.View") ? "plans" : "reports");
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [workshopStock, setWorkshopStock] = useState<WorkshopStock | null>(null);
  const [semiFinishedIssues, setSemiFinishedIssues] = useState<SemiFinishedIssue[]>([]);
  const [items, setItems] = useState<ItemListRow[]>([]);
  const [foundation, setFoundation] = useState<FoundationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [detail, setDetail] = useState<PlanRow | ReportRow | null>(null);
  const [search, setSearch] = useState("");
  const [planForm, setPlanForm] = useState({
    number: "", planDate: today(), plannedStartDate: today(), plannedEndDate: "", notes: "",
    lines: [blankPlanLine()]
  });
  const [reportForm, setReportForm] = useState({
    number: "", reportDate: today(), productionLineId: "", shiftId: "", notes: "",
    lines: [blankReportLine()]
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [planData, reportData, workshopData, issueData, itemData, foundationData] = await Promise.all([
        apiRequest<PlanRow[]>("/api/production/plans"),
        apiRequest<ReportRow[]>("/api/production/reports"),
        apiRequest<WorkshopStock>(`/api/production/workshop-stock?asOf=${today()}`),
        apiRequest<SemiFinishedIssue[]>("/api/production/available-semi-finished-issues"),
        apiRequest<ItemListRow[]>("/api/items?type=Product"),
        apiRequest<FoundationData>("/api/master-data/foundation")
      ]);
      setPlans(planData);
      setReports(reportData);
      setWorkshopStock(workshopData);
      setSemiFinishedIssues(issueData);
      setItems(itemData.filter((x) => x.isActive));
      setFoundation(foundationData);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được dữ liệu sản xuất.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (tab !== "reports" || !reportForm.reportDate) return;
    void apiRequest<WorkshopStock>(`/api/production/workshop-stock?asOf=${reportForm.reportDate}`)
      .then(setWorkshopStock)
      .catch(() => undefined);
  }, [reportForm.reportDate, tab]);

  const itemOptions = useMemo(() => items.map((item) => ({
    value: item.id,
    label: `${item.code} – ${item.name}`,
    selectedLabel: item.name,
    keywords: `${item.code} ${item.name} ${item.specificationSymbol ?? ""}`
  })), [items]);
  const uomOptions = useMemo(() => (foundation?.uoms ?? []).map((uom) => ({
    value: uom.id, label: `${uom.code} – ${uom.name}`, selectedLabel: uom.name, keywords: `${uom.code} ${uom.name} ${uom.symbol}`
  })), [foundation]);
  const lineOptions = useMemo(() => (foundation?.lines ?? []).map((line) => ({
    value: line.id, label: `${line.code} – ${line.name}`, keywords: `${line.code} ${line.name}`
  })), [foundation]);
  const shiftOptions = useMemo(() => (foundation?.shifts ?? []).map((shift) => ({
    value: shift.id, label: `${shift.code} – ${shift.name}`, keywords: `${shift.code} ${shift.name}`
  })), [foundation]);
  const planLineOptions = useMemo(() => plans
    .filter((plan) => plan.status === "InProgress")
    .flatMap((plan) => plan.lines.filter((line) => line.remainingQuantity > 0).map((line) => ({
        value: line.id,
        label: `${plan.number} – ${line.productCode} – ${line.productName} – còn ${formatNumber(line.remainingQuantity)} ${line.uomName}${line.overPlanQuantity > 0 ? ` – vượt ${formatNumber(line.overPlanQuantity)}` : ""}`,
        keywords: `${plan.number} ${line.productCode} ${line.productName}`
      }))), [plans]);
  const semiFinishedIssueOptions = useMemo(() => semiFinishedIssues.map((issue) => ({
    value: `${issue.id}:${issue.productItemId}`,
    label: `${issue.number} – ${issue.productCode} – đã xuất ${formatNumber(issue.issuedQuantity)} ${issue.uomName}`,
    keywords: `${issue.number} ${issue.productCode} ${issue.productName}`
  })), [semiFinishedIssues]);

  const semiFinishedIssueValue = (line: ReportDraftLine) =>
    line.sourceInventoryDocumentId && line.productItemId
      ? `${line.sourceInventoryDocumentId}:${line.productItemId}`
      : "";

  const isBagUom = (uomId: string) =>
    foundation?.uoms.find((uom) => uom.id === uomId)?.code === "BICH";

  const calculateWorkshopLine = (line: ReportDraftLine, index: number) => {
    const item = items.find((x) => x.id === line.productItemId);
    const pieceUom = foundation?.uoms.find((x) => x.code === "CAI");
    const bagUom = foundation?.uoms.find((x) => x.code === "BICH");
    const finishedUom = foundation?.uoms.find((x) => x.id === line.uomId);
    if (!item || !pieceUom || !bagUom || !finishedUom) return null;
    const factor = (uomId: string) => {
      if (uomId === pieceUom.id) return 1;
      const configured = item.pieceFactors?.find((x) => x.uomId === uomId)?.factor;
      return configured ?? null;
    };
    const bagFactor = factor(bagUom.id);
    const finishedFactor = factor(line.uomId);
    if (bagFactor == null || finishedFactor == null) return null;
    const stock = workshopStock?.items.find((x) => x.itemId === line.productItemId);
    let blankBalance = stock?.blankBalance ?? 0;
    let current = { producedBags: 0, issuedBags: 0, returnedBags: 0, remainingBlank: blankBalance };
    for (let lineIndex = 0; lineIndex <= index; lineIndex += 1) {
      const candidate = reportForm.lines[lineIndex];
      if (candidate.productItemId !== line.productItemId) continue;
      const candidateFactor = factor(candidate.uomId);
      if (candidateFactor == null) return null;
      const finishedBags = candidate.finishedQuantity * candidateFactor / bagFactor;
      if (candidate.processType === "FromBlanks") {
        const producedBags = Math.max(0, candidate.semiFinishedQuantity);
        blankBalance = blankBalance + candidate.machineQuantity - producedBags * bagFactor - candidate.finishedQuantity * candidateFactor - candidate.rejectedQuantity;
        current = { producedBags, issuedBags: 0, returnedBags: 0, remainingBlank: blankBalance };
      } else {
        const issuedBags = semiFinishedIssues.find((x) => x.id === candidate.sourceInventoryDocumentId && x.productItemId === candidate.productItemId)?.issuedQuantity ?? 0;
        current = { producedBags: 0, issuedBags, returnedBags: issuedBags - finishedBags - candidate.damagedSemiFinishedQuantity, remainingBlank: blankBalance };
      }
    }
    return current;
  };

  const calculateReportLine = (line: ReportDraftLine) => calculateWorkshopLine(line, reportForm.lines.indexOf(line));

  const openCreate = () => {
    setError(""); setEditingId("");
    if (tab === "plans") {
      setPlanForm({ number: "", planDate: today(), plannedStartDate: today(), plannedEndDate: "", notes: "", lines: [blankPlanLine()] });
    } else if (tab === "reports") {
      setReportForm({
        number: "", reportDate: today(), productionLineId: foundation?.lines[0]?.id ?? "",
        shiftId: foundation?.shifts[0]?.id ?? "", notes: "", lines: [blankReportLine()]
      });
    }
    setShowForm(true);
  };

  const openEditPlan = (row: PlanRow) => {
    setError("");
    setEditingId(row.id);
    setPlanForm({
      number: row.number, planDate: row.planDate, plannedStartDate: row.plannedStartDate ?? "",
      plannedEndDate: row.plannedEndDate ?? "", notes: row.notes ?? "",
      lines: row.lines.map((line) => ({
        clientKey: nextDraftLineKey(),
        id: line.id, productItemId: line.productItemId, plannedQuantity: line.plannedQuantity,
        uomId: line.uomId, reportedQuantity: line.reportedQuantity
      }))
    });
    setShowForm(true);
  };

  const openEditReport = (row: ReportRow) => {
    setError("");
    const currentSources = row.lines
      .filter((line) => line.sourceInventoryDocumentId && !semiFinishedIssues.some((x) =>
        x.id === line.sourceInventoryDocumentId && x.productItemId === line.productItemId))
      .map((line) => ({
        id: line.sourceInventoryDocumentId!, number: line.sourceInventoryDocumentNumber ?? "Phiếu xuất BTP",
        documentDate: row.reportDate, productItemId: line.productItemId, productCode: line.productCode,
        productName: line.productName, issuedQuantity: line.issuedSemiFinishedQuantity,
        uomId: foundation?.uoms.find((x) => x.code === "BICH")?.id ?? "", uomName: "Bịch"
      }));
    if (currentSources.length) setSemiFinishedIssues([...semiFinishedIssues, ...currentSources]);
    setEditingId(row.id);
    setReportForm({
      number: row.number, reportDate: row.reportDate, productionLineId: row.productionLineId ?? "",
      shiftId: row.shiftId ?? "", notes: row.notes ?? "",
      lines: row.lines.map((line) => ({
        clientKey: nextDraftLineKey(),
        productionPlanLineId: line.productionPlanLineId ?? "", productItemId: line.productItemId,
        uomId: line.uomId, productionLotCode: line.productionLotCode, machineQuantity: line.machineQuantity,
        processType: line.processType ?? "FromBlanks", sourceInventoryDocumentId: line.sourceInventoryDocumentId ?? "",
        semiFinishedQuantity: line.semiFinishedQuantity, finishedQuantity: line.finishedQuantity,
        rejectedQuantity: line.rejectedQuantity, damagedSemiFinishedQuantity: line.damagedSemiFinishedQuantity ?? 0
      }))
    });
    setShowForm(true);
  };

  const updatePlanLine = (index: number, patch: Partial<PlanDraftLine>) => {
    const lines = [...planForm.lines];
    const next = { ...lines[index], ...patch };
    if (patch.productItemId) next.uomId = items.find((x) => x.id === patch.productItemId)?.baseUomId ?? next.uomId;
    lines[index] = next;
    setPlanForm({ ...planForm, lines });
  };

  const updateReportLine = (index: number, patch: Partial<ReportDraftLine>) => {
    const lines = [...reportForm.lines];
    const next = { ...lines[index], ...patch };
    if (patch.processType === "FromBlanks") {
      next.sourceInventoryDocumentId = ""; next.damagedSemiFinishedQuantity = 0;
    } else if (patch.processType === "BoxFromWarehouseSemiFinished") {
      next.machineQuantity = 0; next.semiFinishedQuantity = 0; next.rejectedQuantity = 0;
    }
    if (patch.sourceInventoryDocumentId && patch.productItemId === undefined) {
      const source = semiFinishedIssues.find((x) => x.id === patch.sourceInventoryDocumentId);
      if (source) next.productItemId = source.productItemId;
    }
    if (patch.productionPlanLineId !== undefined) {
      const planLine = plans.flatMap((x) => x.lines).find((x) => x.id === patch.productionPlanLineId);
      if (planLine) {
        next.productItemId = planLine.productItemId;
        next.uomId = planLine.uomId;
        if (isBagUom(planLine.uomId)) {
          next.productionLotCode = "";
          next.finishedQuantity = 0;
        }
      }
    } else if (patch.productItemId) {
      next.uomId = items.find((x) => x.id === patch.productItemId)?.baseUomId ?? next.uomId;
    }
    lines[index] = next;
    setReportForm({ ...reportForm, lines });
  };

  const selectSemiFinishedIssue = (index: number, optionValue: string) => {
    const source = semiFinishedIssues.find((issue) => `${issue.id}:${issue.productItemId}` === optionValue);
    const current = reportForm.lines[index];
    const selectedPlan = plans.flatMap((plan) => plan.lines).find((line) => line.id === current.productionPlanLineId);
    updateReportLine(index, {
      sourceInventoryDocumentId: source?.id ?? "",
      productItemId: source?.productItemId ?? "",
      productionPlanLineId: source && selectedPlan?.productItemId === source.productItemId
        ? current.productionPlanLineId
        : ""
    });
  };

  const save = async () => {
    setError("");
    if (tab === "plans") {
      const invalidIndex = planForm.lines.findIndex((line) => !line.productItemId || !line.uomId || line.plannedQuantity <= 0);
      if (invalidIndex >= 0) {
        setError(`Dòng ${invalidIndex + 1}: Sản phẩm, đơn vị và số lượng lớn hơn 0 là bắt buộc.`);
        return;
      }
    } else {
      const invalidIndex = reportForm.lines.findIndex((line) =>
        line.processType === "FromBlanks"
          ? !line.productionPlanLineId || !line.productItemId || !line.uomId
          : !line.sourceInventoryDocumentId || !line.productItemId || !line.uomId);
      if (invalidIndex >= 0) {
        setError(`Dòng ${invalidIndex + 1}: Chưa chọn đủ chứng từ nguồn, sản phẩm hoặc đơn vị.`);
        return;
      }
    }
    try {
      if (tab === "plans") {
        const body = {
          ...planForm,
          plannedStartDate: planForm.plannedStartDate || null,
          plannedEndDate: planForm.plannedEndDate || null,
          lines: planForm.lines.map((line) => ({
            id: line.id ?? null,
            productItemId: line.productItemId,
            plannedQuantity: line.plannedQuantity,
            uomId: line.uomId
          }))
        };
        await apiRequest(editingId ? `/api/production/plans/${editingId}` : "/api/production/plans", {
          method: editingId ? "PUT" : "POST", body: JSON.stringify(body)
        });
      } else {
        const orderedLines = [
          ...reportForm.lines.filter((line) => line.processType === "FromBlanks"),
          ...reportForm.lines.filter((line) => line.processType === "BoxFromWarehouseSemiFinished")
        ];
        await apiRequest(editingId ? `/api/production/reports/${editingId}` : "/api/production/reports", {
          method: editingId ? "PUT" : "POST", body: JSON.stringify({
            ...reportForm,
            productionLineId: reportForm.productionLineId || null,
            shiftId: reportForm.shiftId || null,
            lines: orderedLines.map(({ clientKey: _clientKey, ...line }) => ({
              ...line,
              productionPlanLineId: line.productionPlanLineId || null,
              sourceInventoryDocumentId: line.sourceInventoryDocumentId || null
            }))
          })
        });
      }
      setShowForm(false);
      setMessage(editingId ? "Đã lưu thay đổi." : "Đã lưu bản Nháp.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được chứng từ sản xuất.");
    }
  };

  const action = async (kind: "plan" | "report", id: string, operation: "complete" | "cancel") => {
    setError("");
    try {
      await apiRequest(`/api/production/${kind}/${id}/${operation}`, { method: "POST" });
      setMessage(operation === "complete" ? (kind === "report" ? "Báo cáo đã được gửi và khóa." : "Kế hoạch đã chuyển sang Đang thực hiện.") : "Chứng từ Nháp đã hủy.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không thực hiện được thao tác.");
    }
  };

  if (loading && !foundation) return <LoadingState />;
  if (error && !foundation) return <ErrorState message={error} retry={load} />;

  return (
    <div className="module-page">
      <PageHeader
        eyebrow="KẾ HOẠCH VÀ SẢN LƯỢNG"
        title="Sản xuất"
        description="Một kế hoạch và một báo cáo cuối ca có thể gồm nhiều sản phẩm; mỗi sản phẩm được theo dõi riêng theo số lô."
        action={tab !== "workshop" && hasPermission(`${tab === "plans" ? "Planning" : "Production"}.Create`) ? <button className="primary-button" onClick={openCreate}><Plus size={18} /> {tab === "plans" ? "Tạo kế hoạch" : "Báo cáo cuối ca"}</button> : undefined}
      />
      <Tabs tabs={productionTabs} value={tab} onChange={(value) => { setTab(value as "plans" | "reports" | "workshop"); setDetail(null); setSearch(""); }} />
      {message && <Message tone="success">{message}</Message>}
      {error && <Message tone="error">{error}</Message>}
      <section className="panel module-panel">
        <QuickSearch
          value={search}
          onChange={setSearch}
          placeholder={tab === "plans" ? "Tìm số kế hoạch, mã/tên sản phẩm hoặc trạng thái..." : tab === "reports" ? "Tìm số báo cáo, ngày, ca, kế hoạch, sản phẩm hoặc số lô..." : "Tìm mã hoặc tên sản phẩm tồn tại xưởng..."}
          resultCount={tab === "plans" ? plans.length : tab === "reports" ? reports.length : workshopStock?.items.length ?? 0}
        />
        {tab === "plans" ? (
          <div className="data-table-wrapper"><table className="data-table"><thead><tr><th className="stt-column">STT</th><th>Số kế hoạch</th><th>Ngày lập</th><th>Danh sách sản phẩm</th><th>Thời gian dự kiến</th><th>Trạng thái</th><th /></tr></thead><tbody>
            {plans.map((row, index) => <tr key={row.id}><td className="stt-column">{index + 1}</td><td><strong>{row.number}</strong><small className="table-subtext">{row.lineCount} dòng</small></td><td>{formatDate(row.planDate)}</td><td>{row.lines.map((line) => <div className={`stacked-line progress-line ${planProgressClass(line)}`} key={line.id}><strong>{line.productCode}</strong> – {line.productName}: {formatNumber(line.plannedQuantity)} {line.uomName}<small>Đã báo cáo: {formatNumber(line.reportedQuantity)} · Còn lại: {formatNumber(line.remainingQuantity)}{line.overPlanQuantity > 0 ? ` · Vượt KH: ${formatNumber(line.overPlanQuantity)}` : ""}</small></div>)}</td><td>{formatDate(row.plannedStartDate)} – {formatDate(row.plannedEndDate)}</td><td><StatusBadge status={row.status} /></td><td><div className="table-actions"><button onClick={() => setDetail(row)} title="Xem chi tiết"><Eye size={17} /></button>{(row.status === "Draft" || row.status === "InProgress") && hasPermission("Planning.Edit") && <button onClick={() => openEditPlan(row)} title={row.status === "Draft" ? "Sửa bản Nháp" : "Sửa kế hoạch đang thực hiện"}><Pencil size={17} /></button>}{row.status === "Draft" && hasPermission("Planning.Complete") && <button className="success-action" onClick={() => void action("plan", row.id, "complete")} title="Bắt đầu thực hiện"><Check size={17} /></button>}{row.status === "Draft" && hasPermission("Planning.Cancel") && <button className="danger-action" onClick={() => void action("plan", row.id, "cancel")} title="Hủy bản Nháp"><RotateCcw size={17} /></button>}</div></td></tr>)}
            {!plans.length && <tr><td colSpan={7}><div className="empty-state">Chưa có kế hoạch sản xuất.</div></td></tr>}
          </tbody></table></div>
        ) : tab === "reports" ? (
          <div className="data-table-wrapper"><table className="data-table numeric-table"><thead><tr><th className="stt-column">STT</th><th>Số báo cáo</th><th>Ngày / Ca</th><th>Danh sách sản phẩm</th><th className="number-cell">SL trên máy</th><th className="number-cell">BTP mới</th><th className="number-cell">Thành phẩm</th><th className="number-cell">Phôi còn</th><th className="number-cell">Lỗi/phế</th><th>Trạng thái</th><th /></tr></thead><tbody>
            {reports.map((row, index) => <tr key={row.id}><td className="stt-column">{index + 1}</td><td><strong>{row.number}</strong><small className="table-subtext">{row.lineCount} dòng công đoạn</small></td><td>{formatDate(row.reportDate)}<small className="table-subtext">{row.shiftName || "Không chọn ca"}</small></td><td>{row.lines.map((line) => <div className="stacked-line" key={line.id}><strong>{line.productCode}</strong> – {line.productName}<small>{line.processType === "FromBlanks" ? "Sản xuất từ phôi" : `Đóng hộp từ ${line.sourceInventoryDocumentNumber ?? "BTP xuất kho"}`}{line.productionLotCode ? ` · Lô ${line.productionLotCode}` : ""}{line.planNumber ? ` · ${line.planNumber}` : ""}</small></div>)}</td><td className="number-cell">{formatNumber(row.machineQuantity)}</td><td className="number-cell">{formatNumber(row.semiFinishedQuantity)}</td><td className="number-cell"><strong>{formatNumber(row.finishedQuantity)}</strong></td><td className="number-cell"><strong>{formatNumber(row.remainingBlankQuantity)}</strong></td><td className="number-cell">{formatNumber(row.rejectedQuantity)}</td><td><StatusBadge status={row.status} /></td><td><div className="table-actions"><button onClick={() => setDetail(row)} title="Xem chi tiết"><Eye size={17} /></button>{row.status === "Draft" && hasPermission("Production.Edit") && <button onClick={() => openEditReport(row)} title="Sửa bản Nháp"><Pencil size={17} /></button>}{row.status === "Draft" && hasPermission("Production.Complete") && <button className="success-action" onClick={() => void action("report", row.id, "complete")} title="Gửi báo cáo"><Check size={17} /></button>}{row.status === "Draft" && hasPermission("Production.Cancel") && <button className="danger-action" onClick={() => void action("report", row.id, "cancel")} title="Hủy bản Nháp"><RotateCcw size={17} /></button>}</div></td></tr>)}
            {!reports.length && <tr><td colSpan={11}><div className="empty-state">Chưa có báo cáo cuối ca.</div></td></tr>}
          </tbody></table></div>
        ) : (
          <div className="data-table-wrapper"><table className="data-table numeric-table"><thead><tr><th className="stt-column">STT</th><th>Mã sản phẩm</th><th>Tên sản phẩm</th><th className="number-cell">Tồn phôi tại xưởng</th><th className="center-cell">Đơn vị</th></tr></thead><tbody>
            {(workshopStock?.items ?? []).map((row, index) => <tr key={row.itemId}><td className="stt-column">{index + 1}</td><td><strong>{row.itemCode}</strong></td><td>{row.itemName}</td><td className="number-cell"><strong>{formatNumber(row.blankBalance)}</strong></td><td className="center-cell">{workshopStock?.blankUom ?? "Cái"}</td></tr>)}
            {!workshopStock?.items.length && <tr><td colSpan={5}><div className="empty-state">Chưa có sản phẩm trong Danh mục.</div></td></tr>}
          </tbody></table></div>
        )}
      </section>

      {showForm && tab === "plans" && <Modal title={`${editingId ? "Sửa" : "Tạo"} kế hoạch sản xuất`} onClose={() => setShowForm(false)} wide error={error}>
        <div className="form-grid four">
          <label>Số kế hoạch<input disabled={!!editingId} placeholder="Tự động" value={planForm.number} onChange={(e) => setPlanForm({ ...planForm, number: e.target.value })} /></label>
          <label>Ngày lập *<input type="date" value={planForm.planDate} onChange={(e) => setPlanForm({ ...planForm, planDate: e.target.value })} /></label>
          <label>Ngày bắt đầu<input type="date" value={planForm.plannedStartDate} onChange={(e) => setPlanForm({ ...planForm, plannedStartDate: e.target.value })} /></label>
          <label>Ngày hoàn thành<input type="date" value={planForm.plannedEndDate} onChange={(e) => setPlanForm({ ...planForm, plannedEndDate: e.target.value })} /></label>
        </div>
        <div className="line-editor"><div className="line-editor-header"><strong>Danh sách sản phẩm</strong></div><div className="data-table-wrapper"><table className="data-table line-table production-plan-line-table"><thead><tr><th>STT</th><th>Sản phẩm *</th><th>Số lượng *</th><th>Đơn vị *</th><th /></tr></thead><tbody>{planForm.lines.map((line, index) => <tr key={line.id ?? line.clientKey} className={!!error && (!line.productItemId || !line.uomId || line.plannedQuantity <= 0) ? "validation-row-error" : undefined}><td>{index + 1}</td><td><SearchSelect value={line.productItemId} options={itemOptions} onChange={(value) => updatePlanLine(index, { productItemId: value })} disabled={(line.reportedQuantity ?? 0) > 0} invalid={!!error && !line.productItemId} errorText={`Dòng ${index + 1}: Chọn sản phẩm.`} /></td><td><input type="number" min="0.000001" required aria-invalid={!!error && line.plannedQuantity <= 0} step="any" value={line.plannedQuantity} onChange={(e) => updatePlanLine(index, { plannedQuantity: Number(e.target.value) })} />{!!error && line.plannedQuantity <= 0 && <small className="field-error-text">Số lượng phải lớn hơn 0.</small>}</td><td><SearchSelect value={line.uomId} options={uomOptions} onChange={(value) => updatePlanLine(index, { uomId: value })} placeholder="Tìm đơn vị" disabled={(line.reportedQuantity ?? 0) > 0} invalid={!!error && !line.uomId} errorText="Chọn đơn vị." /></td><td><button className="remove-icon" title={(line.reportedQuantity ?? 0) > 0 ? "Dòng đã báo cáo không được xóa" : "Xóa dòng"} disabled={planForm.lines.length === 1 || (line.reportedQuantity ?? 0) > 0} onClick={() => setPlanForm({ ...planForm, lines: planForm.lines.filter((_, i) => i !== index) })}><Trash2 size={16} /></button></td></tr>)}</tbody></table></div><div className="add-line-below"><button className="secondary-button" onClick={() => setPlanForm({ ...planForm, lines: [...planForm.lines, blankPlanLine()] })}><Plus size={16} /> Thêm sản phẩm</button></div></div>
        <label className="full-field">Ghi chú<textarea value={planForm.notes} onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })} /></label>
        <div className="form-actions"><button className="secondary-button" onClick={() => setShowForm(false)}>Đóng</button><button className="primary-button" onClick={() => void save()}>{editingId ? "Lưu thay đổi" : "Lưu bản Nháp"}</button></div>
      </Modal>}

      {showForm && tab === "reports" && <Modal title={`${editingId ? "Sửa" : "Tạo"} báo cáo sản lượng cuối ca`} subtitle="Xưởng chỉ theo dõi tồn phôi; BTP mới nhập kho và BTP đóng hộp phải lấy từ Phiếu xuất kho." onClose={() => setShowForm(false)} wide error={error}>
        <div className="form-grid four">
          <label>Số báo cáo<input disabled={!!editingId} placeholder="Tự động" value={reportForm.number} onChange={(e) => setReportForm({ ...reportForm, number: e.target.value })} /></label>
          <label>Ngày báo cáo *<input type="date" value={reportForm.reportDate} onChange={(e) => setReportForm({ ...reportForm, reportDate: e.target.value })} /></label>
          <label>Máy/Line<SearchSelect value={reportForm.productionLineId} options={lineOptions} onChange={(value) => setReportForm({ ...reportForm, productionLineId: value })} placeholder="Tìm máy/line" /></label>
          <label>Ca<SearchSelect value={reportForm.shiftId} options={shiftOptions} onChange={(value) => setReportForm({ ...reportForm, shiftId: value })} placeholder="Tìm ca" /></label>
        </div>
        <div className="line-editor production-process-block">
          <div className="line-editor-header">
            <div><strong>1. Sản xuất từ phôi</strong><small className="table-subtext">Mỗi sản phẩm thực tế làm trong ngày nhập một dòng; tất cả dòng phải chọn từ Kế hoạch đang thực hiện.</small></div>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table line-table report-line-table">
              <thead>
                <tr>
                  <th>STT</th><th>Kế hoạch đang thực hiện *</th><th>Sản phẩm *</th><th>Đơn vị TP *</th><th>Số lô TP</th>
                  <th>SL máy/phôi</th><th>BTP mới</th><th>Thành phẩm</th><th>Lỗi/phế phôi</th><th>Phôi còn</th><th />
                </tr>
              </thead>
              <tbody>{reportForm.lines.map((line, index) => ({ line, index })).filter(({ line }) => line.processType === "FromBlanks").map(({ line, index }, blockIndex) => {
                const calculation = calculateReportLine(line);
                const remaining = calculation?.remainingBlank ?? null;
                const bagPlan = isBagUom(line.uomId);
                return <tr key={line.clientKey}>
                  <td>{blockIndex + 1}</td>
                  <td>
                    <SearchSelect value={line.productionPlanLineId} options={planLineOptions} onChange={(value) => updateReportLine(index, { productionPlanLineId: value })} placeholder="Nhập số KH hoặc sản phẩm" invalid={!!error && !line.productionPlanLineId} errorText={`Dòng ${index + 1}: Chọn kế hoạch đang thực hiện.`} />
                    {line.productionPlanLineId && <small className="table-subtext">{bagPlan ? "KH Bịch: ghi tại BTP mới" : "Thành phẩm trừ kế hoạch đã chọn"}</small>}
                  </td>
                  <td><SearchSelect value={line.productItemId} options={itemOptions} onChange={() => undefined} placeholder="Tự lấy theo kế hoạch" disabled /></td>
                  <td><SearchSelect value={line.uomId} options={uomOptions} onChange={() => undefined} placeholder="Tự lấy theo kế hoạch" disabled /></td>
                  <td><input value={line.productionLotCode} onChange={(e) => updateReportLine(index, { productionLotCode: e.target.value })} disabled={bagPlan} placeholder={bagPlan ? "Không cần số lô" : "Bắt buộc khi có TP"} /></td>
                  <td><input type="number" min="0" step="any" value={line.machineQuantity} onChange={(e) => updateReportLine(index, { machineQuantity: Number(e.target.value) })} /></td>
                  <td><input type="number" min="0" step="any" value={line.semiFinishedQuantity} onChange={(e) => updateReportLine(index, { semiFinishedQuantity: Number(e.target.value) })} /></td>
                  <td><input type="number" min="0" step="any" value={line.finishedQuantity} onChange={(e) => updateReportLine(index, { finishedQuantity: Number(e.target.value) })} disabled={bagPlan} /></td>
                  <td><input type="number" min="0" step="any" value={line.rejectedQuantity} onChange={(e) => updateReportLine(index, { rejectedQuantity: Number(e.target.value) })} /></td>
                  <td><input readOnly value={remaining == null ? "Chưa có quy đổi" : formatNumber(remaining)} className={remaining != null && remaining < 0 ? "invalid-value" : ""} /></td>
                  <td><button className="remove-icon" disabled={reportForm.lines.length === 1} onClick={() => setReportForm({ ...reportForm, lines: reportForm.lines.filter((_, i) => i !== index) })}><Trash2 size={16} /></button></td>
                </tr>;
              })}{!reportForm.lines.some((line) => line.processType === "FromBlanks") && <tr><td colSpan={11}><div className="empty-state">Không có dòng sản xuất từ phôi trong báo cáo này.</div></td></tr>}</tbody>
            </table>
          </div>
          <div className="add-line-below"><button className="secondary-button" onClick={() => setReportForm({ ...reportForm, lines: [...reportForm.lines, blankReportLine("FromBlanks")] })}><Plus size={16} /> Thêm dòng sản xuất</button></div>
        </div>
        <div className="line-editor production-process-block">
          <div className="line-editor-header">
            <div><strong>2. Đóng hộp từ BTP xuất kho</strong><small className="table-subtext">BTP phải lấy từ Phiếu xuất kho; hệ thống tự tính số đã dùng và số phải trả kho.</small></div>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table line-table report-line-table">
              <thead><tr>
                <th>STT</th><th>Phiếu xuất BTP *</th><th>Kế hoạch TP liên quan</th><th>Sản phẩm *</th><th>Đơn vị TP *</th><th>Số lô TP</th>
                <th>BTP đã xuất</th><th>Thành phẩm</th><th>BTP hỏng</th><th>BTP trả kho</th><th />
              </tr></thead>
              <tbody>{reportForm.lines.map((line, index) => ({ line, index })).filter(({ line }) => line.processType === "BoxFromWarehouseSemiFinished").map(({ line, index }, blockIndex) => {
                const calculation = calculateReportLine(line);
                const bagPlan = isBagUom(line.uomId);
                return <tr key={line.clientKey}>
                  <td>{blockIndex + 1}</td>
                  <td><SearchSelect value={semiFinishedIssueValue(line)} options={semiFinishedIssueOptions} onChange={(value) => selectSemiFinishedIssue(index, value)} placeholder="Chọn phiếu và sản phẩm BTP" invalid={!!error && !line.sourceInventoryDocumentId} errorText={`Dòng ${index + 1}: Chọn Phiếu xuất BTP.`} /></td>
                  <td><SearchSelect value={line.productionPlanLineId} options={planLineOptions} onChange={(value) => updateReportLine(index, { productionPlanLineId: value })} placeholder="Có thể bỏ trống" /></td>
                  <td><SearchSelect value={line.productItemId} options={itemOptions} onChange={() => undefined} placeholder="Tự lấy theo Phiếu xuất BTP" disabled /></td>
                  <td><SearchSelect value={line.uomId} options={uomOptions} onChange={(value) => updateReportLine(index, { uomId: value })} placeholder="Tìm đơn vị thành phẩm" disabled={!!line.productionPlanLineId} /></td>
                  <td><input value={line.productionLotCode} onChange={(e) => updateReportLine(index, { productionLotCode: e.target.value })} disabled={bagPlan} placeholder={bagPlan ? "Không cần số lô" : "Bắt buộc khi có TP"} /></td>
                  <td><input readOnly value={formatNumber(calculation?.issuedBags ?? 0)} /></td>
                  <td><input type="number" min="0" step="any" value={line.finishedQuantity} onChange={(e) => updateReportLine(index, { finishedQuantity: Number(e.target.value) })} disabled={bagPlan} /></td>
                  <td><input type="number" min="0" step="any" value={line.damagedSemiFinishedQuantity} onChange={(e) => updateReportLine(index, { damagedSemiFinishedQuantity: Number(e.target.value) })} /></td>
                  <td><input readOnly value={formatNumber(calculation?.returnedBags ?? 0)} className={(calculation?.returnedBags ?? 0) < 0 ? "invalid-value" : ""} /></td>
                  <td><button className="remove-icon" disabled={reportForm.lines.length === 1} onClick={() => setReportForm({ ...reportForm, lines: reportForm.lines.filter((_, i) => i !== index) })}><Trash2 size={16} /></button></td>
                </tr>;
              })}{!reportForm.lines.some((line) => line.processType === "BoxFromWarehouseSemiFinished") && <tr><td colSpan={11}><div className="empty-state">Chưa có công đoạn đóng hộp từ BTP xuất kho.</div></td></tr>}</tbody>
            </table>
          </div>
          <div className="add-line-below"><button className="secondary-button" onClick={() => setReportForm({ ...reportForm, lines: [...reportForm.lines, blankReportLine("BoxFromWarehouseSemiFinished")] })}><Plus size={16} /> Thêm dòng đóng hộp</button></div>
        </div>
        <label className="full-field">Ghi chú<textarea value={reportForm.notes} onChange={(e) => setReportForm({ ...reportForm, notes: e.target.value })} /></label>
        <div className="form-actions"><button className="secondary-button" onClick={() => setShowForm(false)}>Đóng</button><button className="primary-button" onClick={() => void save()}>{editingId ? "Lưu thay đổi" : "Lưu bản Nháp"}</button></div>
      </Modal>}

      {detail && "lines" in detail && <Modal title={detail.number} subtitle={tab === "plans" ? "Chi tiết Kế hoạch sản xuất" : "Chi tiết Báo cáo sản lượng cuối ca"} onClose={() => setDetail(null)} wide>
        <div className="document-overview"><span>Ngày: <strong>{formatDate(tab === "plans" ? (detail as PlanRow).planDate : (detail as ReportRow).reportDate)}</strong></span><span>Trạng thái: <StatusBadge status={detail.status} /></span><span>Số dòng: <strong>{detail.lines.length}</strong></span></div>
        <div className="data-table-wrapper"><table className="data-table numeric-table production-detail-table">
          <thead>{tab === "plans"
            ? <tr><th className="center-cell">STT</th><th>Mã</th><th>Tên sản phẩm</th><th className="center-cell">Đơn vị</th><th className="number-cell">Kế hoạch</th><th className="number-cell">Đã báo cáo</th><th className="number-cell">Còn lại</th><th className="number-cell">Vượt KH</th></tr>
            : <tr><th>STT</th><th>Công đoạn / Nguồn</th><th>Kế hoạch</th><th>Sản phẩm</th><th>ĐVT</th><th>Lô TP</th><th className="number-cell">Máy/phôi</th><th className="number-cell">BTP mới</th><th className="number-cell">BTP xuất</th><th className="number-cell">Thành phẩm</th><th className="number-cell">BTP hỏng</th><th className="number-cell">BTP trả</th><th className="number-cell">Phế phôi</th><th className="number-cell">Phôi còn</th></tr>}
          </thead>
          <tbody>{tab === "plans" ? (detail as PlanRow).lines.map((line) => <tr className={planProgressClass(line)} key={line.id}><td className="center-cell">{line.lineNo}</td><td><strong>{line.productCode}</strong></td><td>{line.productName}</td><td className="center-cell">{line.uomName}</td><td className="number-cell">{formatNumber(line.plannedQuantity)}</td><td className="number-cell">{formatNumber(line.reportedQuantity)}</td><td className="number-cell"><strong>{formatNumber(line.remainingQuantity)}</strong></td><td className="number-cell"><strong>{formatNumber(line.overPlanQuantity)}</strong></td></tr>) : (detail as ReportRow).lines.map((line) => <tr key={line.id}><td>{line.lineNo}</td><td>{line.processType === "FromBlanks" ? "Sản xuất từ phôi" : `Đóng hộp – ${line.sourceInventoryDocumentNumber ?? "BTP xuất kho"}`}</td><td>{line.planNumber ?? "—"}</td><td><strong>{line.productCode}</strong> – {line.productName}</td><td>{line.uomName}</td><td>{line.productionLotCode || "—"}</td><td className="number-cell">{formatNumber(line.machineQuantity)}</td><td className="number-cell">{formatNumber(line.semiFinishedQuantity)}</td><td className="number-cell">{formatNumber(line.issuedSemiFinishedQuantity)}</td><td className="number-cell"><strong>{formatNumber(line.finishedQuantity)}</strong></td><td className="number-cell">{formatNumber(line.damagedSemiFinishedQuantity)}</td><td className="number-cell">{formatNumber(line.returnedSemiFinishedQuantity)}</td><td className="number-cell">{formatNumber(line.rejectedQuantity)}</td><td className="number-cell"><strong>{formatNumber(line.remainingBlankQuantity)}</strong></td></tr>)}</tbody>
        </table></div>
        {detail.notes && <p><strong>Ghi chú:</strong> {detail.notes}</p>}
        <div className="form-actions"><button className="secondary-button" onClick={() => setDetail(null)}>Đóng</button></div>
      </Modal>}
    </div>
  );
}
